import { useState, useEffect } from "react";
import { useLanguage } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { AppyPaySettings } from "../components/settings/AppyPaySettings";
import { Separator } from "../components/ui/separator";
import { ScrollArea } from "../components/ui/scroll-area";
import { formatCurrency, API_URL } from "../lib/utils";
import { TAX_CODES, EXEMPTION_REASONS as FISCAL_EXEMPTION_REASONS } from "../lib/fiscal";
import { FiscalCompliancePanel } from "../components/settings/FiscalCompliancePanel";
import { EmailSettingsCard } from "../components/settings/EmailSettingsCard";
import { CustomDomainCard } from "../components/settings/CustomDomainCard";
import { HRScheduleCard } from "../components/settings/HRScheduleCard";
import axios from "axios";
import { toast } from "sonner";
import {
  Settings,
  Building2,
  Palette,
  Smartphone,
  Globe,
  Receipt,
  Save,
  Sun,
  Moon,
  FileSpreadsheet,
  Shield,
  AlertCircle,
  CheckCircle,
  Download,
  Upload,
  Percent,
  Calculator,
  Building,
  FileText,
  RefreshCw,
  Key,
  Lock,
  Wifi,
  WifiOff,
  FileCheck,
  Send,
  Clock,
  History,
  AlertTriangle,
  Server,
  Link2,
  ExternalLink,
} from "lucide-react";

// Taxas de IVA e motivos de isenção (tabela oficial SAF-T (AO)) — ver lib/fiscal.js
const EXEMPTION_REASONS = FISCAL_EXEMPTION_REASONS;

// Regimes Fiscais Angola
const TAX_REGIMES = [
  { value: "general", label: "Regime Geral", description: "Empresas com volume de negócios > 350 milhões Kz" },
  { value: "simplified", label: "Regime Simplificado", description: "Pequenas empresas" },
  { value: "exempt", label: "Regime de Isenção", description: "Entidades isentas de IVA" },
  { value: "non_subject", label: "Não Sujeito", description: "Operações fora do âmbito do IVA" },
];

const SettingsPage = () => {
  const { t, language, setLanguage } = useLanguage();
  const { theme, toggleTheme, isDark } = useTheme();
  const { token, isAdmin, user: authUser } = useAuth();
  const companyId = authUser?.company_id;
  const { refreshCurrency } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("company");
  const [saftSummary, setSaftSummary] = useState(null);
  
  const [settings, setSettings] = useState({
    // Dados da Empresa
    restaurant_name: "",
    business_name: "",
    nif: "",
    address: "",
    city: "Luanda",
    postal_code: "0000",
    country: "AO",
    phone: "",
    email: "",
    
    // Logo
    logo_url: "",
    logo_file: "",
    
    // Dados Bancários
    bank_name: "",
    bank_iban: "",
    bank_bic: "",
    bank_account_holder: "",
    
    // Regionais
    currency: "AOA",
    currency_symbol: "Kz",
    exchange_rates: { USD: 900, EUR: 980, MZN: 14 },
    language: "pt",
    timezone: "Africa/Luanda",
    
    // IVA Angola
    tax_regime: "general",
    default_tax_rate: 14.0,
    tax_rates: TAX_CODES,
    exemption_reasons: EXEMPTION_REASONS,
    
    // Faturação
    invoice_series_prefix: "",
    invoice_footer: "",
    print_format: "thermal80",
    invoice_notes: "",
    auto_generate_invoice: true,
    require_customer_nif: false,
    show_bank_details_on_invoice: true,
    show_qr_code_on_invoice: true,
    
    // AGT
    agt_enabled: false,
    agt_environment: "test",
    agt_nif: "",
    agt_username: "",
    agt_password: "",
    agt_certificate_path: "",
    training_mode: false,
    agt_auto_submit: false,
    agt_api_url: "https://efactura.minfin.gov.ao/api/v1",
    
    // SAF-T
    saft_product_id: "Lumora/1.0",
    saft_product_version: "1.0",
    saft_tax_accounting_basis: "F",
    saft_auto_export: false,
  });


  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchSettings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/settings`, {
        headers,
        withCredentials: true,
      });
      setSettings((prev) => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/settings`, settings, {
        headers,
        withCredentials: true,
      });
      toast.success("Definições guardadas com sucesso!");
      fetchSettings();
      
      // Atualizar moeda globalmente
      refreshCurrency();
      
      // Atualizar idioma globalmente se mudou
      if (settings.language !== language) {
        setLanguage(settings.language);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao guardar definições");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validar tipo
    const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Tipo de ficheiro não permitido. Use PNG, JPG, GIF ou WEBP.");
      return;
    }
    
    // Validar tamanho (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Ficheiro muito grande. Máximo 2MB.");
      return;
    }
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      toast.info("A carregar logo...");
      const response = await axios.post(`${API_URL}/settings/upload-logo`, formData, {
        headers: {
          ...headers,
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
      });
      
      setSettings((prev) => ({ ...prev, logo_url: response.data.logo_url }));
      toast.success("Logo carregado com sucesso!");
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === "string" ? `Erro ao carregar logo: ${d}` : `Erro ao carregar logo${error.response?.status ? ` (HTTP ${error.response.status})` : ""}`);
    }
  };

  const handleExportSAFT = async () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const startDate = firstDay.toISOString().split("T")[0];
    const endDate = today.toISOString().split("T")[0];

    try {
      toast.info("A gerar ficheiro SAF-T...");
      const response = await axios.get(
        `${API_URL}/saft/export?start_date=${startDate}&end_date=${endDate}`,
        {
          headers,
          withCredentials: true,
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `SAFT_AO_${settings.nif || "000000000"}_${startDate}_${endDate}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Ficheiro SAF-T exportado!");
    } catch (error) {
      toast.error("Erro ao exportar SAF-T");
    }
  };

  const fetchSAFTSummary = async () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const startDate = firstDay.toISOString().split("T")[0];
    const endDate = today.toISOString().split("T")[0];

    try {
      const response = await axios.get(
        `${API_URL}/saft/summary?start_date=${startDate}&end_date=${endDate}`,
        { headers, withCredentials: true }
      );
      setSaftSummary(response.data);
    } catch (error) {
      console.error("Failed to fetch SAFT summary:", error);
    }
  };

  useEffect(() => {
    if (activeTab === "billing") {
      fetchSAFTSummary();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">{t("settings")}</h1>
          <p className="text-muted-foreground">Configurações do sistema e faturação</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="settings-save-all-btn">
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Guardar Tudo
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full max-w-4xl">
          <TabsTrigger value="company" className="gap-2">
            <Building2 className="w-4 h-4" />
            <span className="hidden sm:inline">Empresa</span>
          </TabsTrigger>
          <TabsTrigger value="billing" className="gap-2">
            <Receipt className="w-4 h-4" />
            <span className="hidden sm:inline">Faturação</span>
          </TabsTrigger>
          <TabsTrigger value="efactura" className="gap-2" data-testid="settings-tab-fiscal">
            <FileCheck className="w-4 h-4" />
            <span className="hidden sm:inline">Fiscal AGT</span>
          </TabsTrigger>
          <TabsTrigger value="regional" className="gap-2">
            <Globe className="w-4 h-4" />
            <span className="hidden sm:inline">Regional</span>
          </TabsTrigger>
          <TabsTrigger value="appearance" className="gap-2">
            <Palette className="w-4 h-4" />
            <span className="hidden sm:inline">Aparência</span>
          </TabsTrigger>
          <TabsTrigger value="payments" className="gap-2" data-testid="settings-tab-payments">
            <Smartphone className="w-4 h-4" />
            <span className="hidden sm:inline">Pagamentos</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="payments" className="space-y-4 mt-6">
          <AppyPaySettings headers={headers} webhookTarget={companyId} />
        </TabsContent>

        {/* EMPRESA */}
        <TabsContent value="company" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Dados da Empresa
              </CardTitle>
              <CardDescription>Informações fiscais e de contacto</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome do Restaurante *</Label>
                  <Input
                    value={settings.restaurant_name}
                    onChange={(e) => setSettings({ ...settings, restaurant_name: e.target.value })}
                    placeholder="Restaurante Exemplo"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nome Comercial</Label>
                  <Input
                    value={settings.business_name || ""}
                    onChange={(e) => setSettings({ ...settings, business_name: e.target.value })}
                    placeholder="Nome comercial (opcional)"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>NIF (Número de Identificação Fiscal) *</Label>
                  <Input
                    value={settings.nif || ""}
                    onChange={(e) => setSettings({ ...settings, nif: e.target.value })}
                    placeholder="000000000"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefone</Label>
                  <Input
                    value={settings.phone || ""}
                    onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                    placeholder="+244 923 456 789"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={settings.email || ""}
                  onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                  placeholder="email@exemplo.ao"
                />
              </div>

              <Separator />

              <div className="space-y-2">
                <Label>Morada</Label>
                <Input
                  value={settings.address || ""}
                  onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                  placeholder="Rua, Número, Bairro"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Cidade</Label>
                  <Input
                    value={settings.city || "Luanda"}
                    onChange={(e) => setSettings({ ...settings, city: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Código Postal</Label>
                  <Input
                    value={settings.postal_code || "0000"}
                    onChange={(e) => setSettings({ ...settings, postal_code: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>País</Label>
                  <Select
                    value={settings.country}
                    onValueChange={(value) => setSettings({ ...settings, country: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AO">Angola</SelectItem>
                      <SelectItem value="PT">Portugal</SelectItem>
                      <SelectItem value="MZ">Moçambique</SelectItem>
                      <SelectItem value="CV">Cabo Verde</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Logo da Empresa */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Logo da Empresa
              </CardTitle>
              <CardDescription>O logo aparecerá nas faturas e documentos</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col md:flex-row gap-6 items-start">
                {/* Preview do Logo */}
                <div className="flex-shrink-0">
                  <div className="w-32 h-32 border-2 border-dashed border-muted-foreground/25 rounded-lg flex items-center justify-center overflow-hidden bg-muted/50">
                    {settings.logo_url ? (
                      <img
                        src={settings.logo_url.startsWith("/api") ? `${API_URL.replace("/api", "")}${settings.logo_url}` : settings.logo_url}
                        alt="Logo"
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <Building2 className="w-12 h-12 text-muted-foreground/50" />
                    )}
                  </div>
                </div>

                {/* Upload e URL */}
                <div className="flex-1 space-y-4">
                  <div className="space-y-2">
                    <Label>Carregar Logo (PNG, JPG, GIF, WEBP - máx. 2MB)</Label>
                    <Input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                      onChange={handleLogoUpload}
                      className="cursor-pointer"
                    />
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>ou</span>
                    <Separator className="flex-1" />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>URL do Logo (externa)</Label>
                    <Input
                      value={settings.logo_url || ""}
                      onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })}
                      placeholder="https://exemplo.com/logo.png"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <CustomDomainCard settings={settings} setSettings={setSettings} onSave={handleSave} saving={saving} />

          <HRScheduleCard settings={settings} setSettings={setSettings} />

          {/* Dados Bancários */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="w-5 h-5" />
                Dados Bancários
              </CardTitle>
              <CardDescription>Informações bancárias para pagamentos (opcional)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome do Banco</Label>
                  <Input
                    value={settings.bank_name || ""}
                    onChange={(e) => setSettings({ ...settings, bank_name: e.target.value })}
                    placeholder="Banco BAI, BFA, etc."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Titular da Conta</Label>
                  <Input
                    value={settings.bank_account_holder || ""}
                    onChange={(e) => setSettings({ ...settings, bank_account_holder: e.target.value })}
                    placeholder="Nome do titular"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>IBAN</Label>
                  <Input
                    value={settings.bank_iban || ""}
                    onChange={(e) => setSettings({ ...settings, bank_iban: e.target.value })}
                    placeholder="AO06 0000 0000 0000 0000 0000 0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>BIC/SWIFT</Label>
                  <Input
                    value={settings.bank_bic || ""}
                    onChange={(e) => setSettings({ ...settings, bank_bic: e.target.value })}
                    placeholder="BAIAAOLU"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* FATURAÇÃO */}
        <TabsContent value="billing" className="space-y-4 mt-6">
          {/* Regime Fiscal */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="w-5 h-5" />
                Regime Fiscal
              </CardTitle>
              <CardDescription>Configuração do regime de IVA da empresa</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Regime de IVA</Label>
                <Select
                  value={settings.tax_regime}
                  onValueChange={(value) => setSettings({ ...settings, tax_regime: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TAX_REGIMES.map((regime) => (
                      <SelectItem key={regime.value} value={regime.value}>
                        <div>
                          <span className="font-medium">{regime.label}</span>
                          <span className="text-xs text-muted-foreground ml-2">- {regime.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Percent className="w-4 h-4" />
                  Taxas de IVA Disponíveis
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {TAX_CODES.map((tax) => (
                    <div
                      key={tax.code}
                      className="flex items-center justify-between p-3 rounded-lg border bg-muted/30"
                    >
                      <div>
                        <Badge variant="outline" className="font-mono mr-2">{tax.code}</Badge>
                        <span className="text-sm">{tax.description}</span>
                      </div>
                      <span className="font-bold font-mono">{tax.percentage}%</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Taxa de IVA Padrão</Label>
                <Select
                  value={settings.default_tax_rate?.toString()}
                  onValueChange={(value) => setSettings({ ...settings, default_tax_rate: parseFloat(value) })}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="14">14% (Taxa Normal)</SelectItem>
                    <SelectItem value="7">7% (Taxa Reduzida)</SelectItem>
                    <SelectItem value="0">0% (Isento)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Motivos de Isenção */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Motivos de Isenção (SAF-T)
              </CardTitle>
              <CardDescription>Códigos de isenção para documentos sem IVA</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-48">
                <div className="space-y-2">
                  {EXEMPTION_REASONS.map((reason) => (
                    <div
                      key={reason.code}
                      className="flex items-center gap-3 p-2 rounded border-l-2 border-primary/30 bg-muted/20"
                    >
                      <Badge variant="secondary" className="font-mono">{reason.code}</Badge>
                      <span className="text-sm">{reason.description}</span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Configurações de Faturação */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5" />
                Configurações de Documentos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Gerar fatura automaticamente ao pagar</Label>
                  <p className="text-sm text-muted-foreground">
                    Uma Fatura-Recibo (FR) será emitida automaticamente
                  </p>
                </div>
                <Switch
                  checked={settings.auto_generate_invoice}
                  onCheckedChange={(checked) =>
                    setSettings({ ...settings, auto_generate_invoice: checked })
                  }
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div>
                  <Label>Exigir NIF do cliente</Label>
                  <p className="text-sm text-muted-foreground">
                    Obrigar preenchimento do NIF em todas as faturas
                  </p>
                </div>
                <Switch
                  checked={settings.require_customer_nif}
                  onCheckedChange={(checked) =>
                    setSettings({ ...settings, require_customer_nif: checked })
                  }
                />
              </div>

              <Separator />

              <div className="space-y-2">
                <Label>Prefixo das Séries</Label>
                <Input
                  value={settings.invoice_series_prefix || ""}
                  onChange={(e) => setSettings({ ...settings, invoice_series_prefix: e.target.value })}
                  placeholder="Ex: REST01"
                  className="w-48"
                />
                <p className="text-xs text-muted-foreground">
                  Prefixo opcional para identificar o estabelecimento
                </p>
              </div>

              <div className="space-y-2">
                <Label>Texto do Rodapé da Fatura</Label>
                <Textarea
                  value={settings.invoice_footer || ""}
                  onChange={(e) => setSettings({ ...settings, invoice_footer: e.target.value })}
                  placeholder="Obrigado pela preferência!"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Formato de Impressão da Fatura</Label>
                <Select
                  value={settings.print_format || "thermal80"}
                  onValueChange={(v) => setSettings({ ...settings, print_format: v })}
                >
                  <SelectTrigger className="w-64" data-testid="print-format-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="thermal80">Talão Térmico (80mm)</SelectItem>
                    <SelectItem value="a4">Folha A4</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Escolha "Talão Térmico" para impressoras de talões (80mm) — formato compacto com todos os dados
                </p>
              </div>
            </CardContent>
          </Card>

          {/* SAF-T Angola */}
          <Card className="border-primary/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-primary" />
                Ficheiro SAF-T (AO)
              </CardTitle>
              <CardDescription>
                Exportação do ficheiro de auditoria fiscal para a AGT
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {saftSummary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="p-3 rounded-lg bg-muted/50 text-center">
                    <p className="text-2xl font-bold">{saftSummary.total_invoices || 0}</p>
                    <p className="text-xs text-muted-foreground">Documentos</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50 text-center">
                    <p className="text-2xl font-bold font-mono">
                      {formatCurrency(saftSummary.total_amount || 0)}
                    </p>
                    <p className="text-xs text-muted-foreground">Volume Total</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50 text-center">
                    <p className="text-2xl font-bold font-mono">
                      {formatCurrency(saftSummary.total_tax || 0)}
                    </p>
                    <p className="text-xs text-muted-foreground">IVA Total</p>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Base Contabilística</Label>
                <Select
                  value={settings.saft_tax_accounting_basis}
                  onValueChange={(value) =>
                    setSettings({ ...settings, saft_tax_accounting_basis: value })
                  }
                >
                  <SelectTrigger className="w-64">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="F">F - Faturação</SelectItem>
                    <SelectItem value="I">I - Integrado (Faturação + Contabilidade)</SelectItem>
                    <SelectItem value="C">C - Contabilidade</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              <div className="flex gap-3">
                <Button onClick={handleExportSAFT} className="gap-2">
                  <Download className="w-4 h-4" />
                  Exportar SAF-T (Mês Atual)
                </Button>
                <Button variant="outline" onClick={() => window.location.href = "/saft"} className="gap-2">
                  <FileSpreadsheet className="w-4 h-4" />
                  Ver Página SAF-T Completa
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* E-FACTURA / FATURAÇÃO ELETRÓNICA */}
        <TabsContent value="efactura" className="space-y-4 mt-6">
          <FiscalCompliancePanel settings={settings} setSettings={setSettings} />
          <EmailSettingsCard settings={settings} setSettings={setSettings} />
        </TabsContent>

        {/* REGIONAL */}
        <TabsContent value="regional" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Configurações Regionais
              </CardTitle>
              <CardDescription>Idioma, moeda e fuso horário</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("language")}</Label>
                  <Select
                    value={settings.language}
                    onValueChange={(value) => {
                      setSettings({ ...settings, language: value });
                      setLanguage(value);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pt">Português</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="es">Español</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Fuso Horário</Label>
                  <Select
                    value={settings.timezone}
                    onValueChange={(value) => setSettings({ ...settings, timezone: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Africa/Luanda">Africa/Luanda (Angola)</SelectItem>
                      <SelectItem value="Europe/Lisbon">Europe/Lisbon (Portugal)</SelectItem>
                      <SelectItem value="Africa/Maputo">Africa/Maputo (Moçambique)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("currency")}</Label>
                  <Select
                    value={settings.currency}
                    onValueChange={(value) => {
                      const symbols = { AOA: "Kz", EUR: "€", USD: "$", MZN: "MT" };
                      setSettings({
                        ...settings,
                        currency: value,
                        currency_symbol: symbols[value] || value,
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AOA">AOA - Kwanza Angolano</SelectItem>
                      <SelectItem value="EUR">EUR - Euro</SelectItem>
                      <SelectItem value="USD">USD - Dólar Americano</SelectItem>
                      <SelectItem value="MZN">MZN - Metical Moçambicano</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Símbolo da Moeda</Label>
                  <Input
                    value={settings.currency_symbol}
                    onChange={(e) => setSettings({ ...settings, currency_symbol: e.target.value })}
                    className="w-24"
                  />
                </div>
              </div>

              {settings.currency !== "AOA" && (
                <div className="p-4 rounded-lg border bg-primary/5 space-y-2">
                  <Label className="font-semibold">Taxa de Câmbio</Label>
                  <p className="text-xs text-muted-foreground">
                    Todos os valores do sistema estão guardados em Kwanza (AOA) e são convertidos automaticamente para {settings.currency} em 100% da aplicação.
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono">1 {settings.currency} =</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      className="w-32"
                      value={(settings.exchange_rates || {})[settings.currency] ?? ""}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          exchange_rates: {
                            ...(settings.exchange_rates || {}),
                            [settings.currency]: parseFloat(e.target.value) || 0,
                          },
                        })
                      }
                      data-testid="exchange-rate-input"
                    />
                    <span className="text-sm font-mono">Kz (AOA)</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* APARÊNCIA */}
        <TabsContent value="appearance" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5" />
                Aparência
              </CardTitle>
              <CardDescription>Tema e preferências visuais</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div className="flex items-center gap-4">
                  {isDark ? (
                    <Moon className="w-6 h-6 text-primary" />
                  ) : (
                    <Sun className="w-6 h-6 text-amber-500" />
                  )}
                  <div>
                    <Label>{t("theme")}</Label>
                    <p className="text-sm text-muted-foreground">
                      {isDark ? "Modo Escuro" : "Modo Claro"}
                    </p>
                  </div>
                </div>
                <Switch checked={isDark} onCheckedChange={toggleTheme} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
